import React, { useEffect } from 'react';
import { ComponentQueryLoader } from '../../../../../shared/component-query-loader';
import { FilePickerDocumentList } from './file-picker-document-list';
import { SelectedCase } from './file-picker';
import {
  StaffDocumentListContainerArchivedCredentialVerificationCaseSummaryDocument,
  StaffDocumentListContainerArchivedIdentityVerificationCaseSummaryDocument,
  CasesDocumentListContainerArchivedSendReportCaseSummaryDocument,
  StaffDocumentListContainerCredentialVerificationCaseV1Document,
  StaffDocumentListContainerIdentityVerificationCaseV1Document,
  CasesDocumentListContainerSendReportCaseV1Document
} from '../../../../../../generated';
import { useLazyQuery } from '@apollo/client';
import { SelectedFileInfo } from './verification-packet-document-picker';

interface FilePickerDocumentListContainerProps {
  selectedCaseInfo: SelectedCase;
  handleFileSelection: (fileInfo: SelectedFileInfo) => void;
}

export const FilePickerDocumentListContainer: React.FC<FilePickerDocumentListContainerProps> = ({ selectedCaseInfo, handleFileSelection }) => {
  const [caseData, setCaseData] = React.useState<any>(null);
  const [caseLoading, setCaseLoading] = React.useState<boolean>(false);
  const [caseError, setCaseError] = React.useState<any>(null);

  const [identityQuery] = useLazyQuery(StaffDocumentListContainerIdentityVerificationCaseV1Document);
  const [credentialQuery] = useLazyQuery(StaffDocumentListContainerCredentialVerificationCaseV1Document);
  const [sendReportQuery] = useLazyQuery(CasesDocumentListContainerSendReportCaseV1Document);
  const [archivedCredentialQuery] = useLazyQuery(StaffDocumentListContainerArchivedCredentialVerificationCaseSummaryDocument);
  const [archivedIdentityQuery] = useLazyQuery(StaffDocumentListContainerArchivedIdentityVerificationCaseSummaryDocument);
  const [archivedSendReportQuery] = useLazyQuery(CasesDocumentListContainerArchivedSendReportCaseSummaryDocument);

  const filterCaseData = (caseData: any, isIdCase: boolean) => {
    const formattedData = caseData.files.filter((file: any) => {
      if (isIdCase) {
        return file.name.endsWith('.pdf');
      }
      return !(file.name.endsWith('photo') || file.name.endsWith('-preview.png') || file.name.endsWith('.json'));
    });
    setCaseData({ ...caseData, files: formattedData });
  };

  const fetchCaseData = async (query: any, variables: any, isIdCase: boolean) => {
    try {
      const { data, loading, error } = await query({ variables });
      const caseDataKey = Object.keys(data)[0]; // Get the first key of the returned data object
      const caseDataTemp = data[caseDataKey];
      if (caseDataTemp?.files) {
        filterCaseData(caseDataTemp, isIdCase);
      }
      setCaseLoading(loading);
      setCaseError(error);
    } catch (error) {
      console.error('Error fetching case data:', error);
      setCaseLoading(false);
      setCaseError(error);
    }
  };

  useEffect(() => {
    if (!selectedCaseInfo || !selectedCaseInfo.id) {
      setCaseData({ files: [] });
      setCaseLoading(false);
      setCaseError(null);
      return;
    }

    const {isArchived, caseType} = selectedCaseInfo;

    if (caseType === 'id-verification') {
      if (isArchived) {
        fetchCaseData(archivedIdentityQuery, { archivedIdentityVerificationCaseSummaryId: selectedCaseInfo.id }, true);
      } else {
        fetchCaseData(identityQuery, { identityVerificationCaseV1Id: selectedCaseInfo.id }, true);
      }
    } else if (caseType === 'credential-verification') {
      if (isArchived) {
        fetchCaseData(archivedCredentialQuery, { archivedCredentialVerificationCaseSummaryId: selectedCaseInfo.id }, false);
      } else {
        fetchCaseData(credentialQuery, { credentialVerificationCaseV1Id: selectedCaseInfo.id }, false);
      }
    } else if (caseType === 'send-report') {
      if (isArchived) {
        fetchCaseData(archivedSendReportQuery, { archivedSendReportCaseSummaryId: selectedCaseInfo.id }, false);
      } else {
        fetchCaseData(sendReportQuery, { sendReportCaseV1Id: selectedCaseInfo.id }, false);
      }
    }
  }, [selectedCaseInfo]);

  return (
    <ComponentQueryLoader
      loading={caseLoading}
      hasData={caseData !== null && caseData.files.length > 0}
      error={caseError}
      hasDataComponent={<FilePickerDocumentList caseData={caseData} handleFileSelection={handleFileSelection} caseType={selectedCaseInfo.caseType} />}
      noDataComponent={<></>}
    />
  );
};